/**
 * 后台接口请求封装。
 *
 * 纪律与要求：
 *  - 统一解包 { code, message, data, traceId }；
 *  - code !== 0 → 抛出 ApiError（可被调用方 catch）+ ElMessage 报错，**并展示 traceId**；
 *  - HTTP 401（或业务码 1002/1107）→ 清理登录态并跳登录页 A01；
 *  - **后台 token 与会员 token 分开存储**，后台接口一律 /api/admin/*；
 *  - 网络不可用（后端未启动）也要给出明确提示，页面自行降级，不白屏。
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ErrCode, type ApiResponse } from '@weidian/contracts'
import { ApiError, isUnauthorizedCode } from '@/api/errors'
import {
  clearAdminTokens,
  clearMemberTokens,
  getAdminAccessToken,
  getAdminRefreshToken,
  getMemberAccessToken,
  setAdminTokens,
} from '@/api/token'
import { notifyError } from '@/api/notify'

/** 需要被识别为「后台域」的路径片段 */
const ADMIN_PATH_MARK = '/admin/'

export interface RequestOptions {
  /** false 时不弹 ElMessage，由调用方自行处理（例如页面内联展示错误态） */
  showError?: boolean
  /** true 时返回完整响应包装（需要 traceId 等元信息时用） */
  rawEnvelope?: boolean
  /** 额外请求头（如 X-Idempotency-Key） */
  headers?: Record<string, string>
  /** 静默 401：不跳登录（登录页自身调用时用） */
  silent401?: boolean
}

/** 登录页地址（hash 路由） */
export const LOGIN_PATH = '/admin/login'

/** 运行时注入的跳登录钩子；main.ts 里绑定到 vue-router，测试里可直接替换 */
let redirectToLogin: (() => void) | null = null

export function setLoginRedirect(fn: (() => void) | null): void {
  redirectToLogin = fn
}

/** 默认跳登录实现：hash 路由下直接改 location.hash，不依赖 router 实例 */
export function defaultLoginRedirect(): void {
  if (typeof window === 'undefined') return
  const target = `#${LOGIN_PATH}`
  if (window.location.hash === target) return
  window.location.hash = target
}

function doRedirectToLogin(): void {
  if (redirectToLogin) redirectToLogin()
  else defaultLoginRedirect()
}

/** 接口不可用（后端未启动 / 网络错误）时置位，供页面展示降级提示 */
let apiUnavailable = false
const unavailableListeners = new Set<(v: boolean) => void>()

export function isApiUnavailable(): boolean {
  return apiUnavailable
}

export function onApiUnavailableChange(fn: (v: boolean) => void): () => void {
  unavailableListeners.add(fn)
  return () => unavailableListeners.delete(fn)
}

function markApiUnavailable(v: boolean): void {
  if (apiUnavailable === v) return
  apiUnavailable = v
  for (const fn of unavailableListeners) fn(v)
}

function baseURL(): string {
  const env = import.meta.env
  return (env && env.VITE_API_BASE) || '/api'
}

export const http: AxiosInstance = axios.create({
  baseURL: baseURL(),
  timeout: 20000,
  headers: { 'Content-Type': 'application/json; charset=utf-8' },
})

// ---------------- 请求拦截：带 token ----------------

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const url = config.url ?? ''
  const isAdmin = url.includes(ADMIN_PATH_MARK)
  const token = isAdmin ? getAdminAccessToken() : getMemberAccessToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  config.headers.set('Accept-Language', 'zh-CN')
  return config
})

// ---------------- 响应拦截：解包 + 错误 ----------------

function buildUrl(config: AxiosRequestConfig | undefined): string {
  const base = config?.baseURL ?? ''
  const url = config?.url ?? ''
  return `${base}${url}`
}

http.interceptors.response.use(
  async (response) => {
    markApiUnavailable(false)

    const body = response.data as ApiResponse<unknown> | undefined

    // 非统一包装（例如导出文件流）直接放行
    if (!body || typeof body !== 'object' || typeof body.code !== 'number') {
      return response
    }

    if (body.code === ErrCode.SUCCESS) {
      return response
    }

    // 业务层的「未登录 / token 过期」同样按 401 处理
    if (isUnauthorizedCode(body.code)) {
      /**
       * ★★ 先尝试**静默续期**再决定要不要踢人（2026-09-29）。
       *    过去这里直接清登录态跳登录页 → 后台 2 小时到点必掉、重开标签页也必掉。
       */
      const retried = await retryAfterSilentRefresh(response.config)
      if (retried) return retried
      handleUnauthorized(response.config)
      return Promise.reject(
        new ApiError(body.code, body.message || '登录已过期，请重新登录', buildUrl(response.config), {
          traceId: body.traceId,
        }),
      )
    }

    return Promise.reject(
      new ApiError(body.code, body.message || '操作失败', buildUrl(response.config), {
        traceId: body.traceId,
        data: body.data,
        httpStatus: response.status,
      }),
    )
  },
  async (error: AxiosError) => {
    const config = error.config as AxiosRequestConfig | undefined
    const url = buildUrl(config)

    if (error.response) {
      const status = error.response.status
      const body = error.response.data as ApiResponse<unknown> | undefined
      if (status === 401) {
        // ★ 同上：HTTP 401 也先试静默续期，成功则重放原请求（只重放一次）
        const retried = await retryAfterSilentRefresh(config)
        if (retried) return retried
        handleUnauthorized(config)
        return Promise.reject(
          new ApiError(ErrCode.UNAUTHORIZED, body?.message || '登录已过期，请重新登录', url, {
            traceId: body?.traceId,
            httpStatus: status,
          }),
        )
      }
      if (status === 403) {
        return Promise.reject(
          new ApiError(ErrCode.FORBIDDEN, body?.message || '没有权限执行此操作', url, {
            traceId: body?.traceId,
            httpStatus: status,
          }),
        )
      }
      return Promise.reject(
        new ApiError(body?.code ?? ErrCode.SYSTEM_BUSY, body?.message || `接口异常（HTTP ${status}）`, url, {
          traceId: body?.traceId,
          httpStatus: status,
        }),
      )
    }

    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      markApiUnavailable(true)
      return Promise.reject(
        new ApiError(ErrCode.DOWNSTREAM_TIMEOUT, '请求超时，请稍后重试', url, { network: true }),
      )
    }

    // 无响应：后端未启动 / 网络不可用 / 跨域
    markApiUnavailable(true)
    return Promise.reject(
      new ApiError(
        ErrCode.SYSTEM_BUSY,
        '接口不可用（后端服务未启动或网络异常），页面已切换为降级展示',
        url,
        { network: true },
      ),
    )
  },
)

/** 401 统一处理：清登录态 + 跳登录。后台与会员 token 分别清理，互不影响。 */
function handleUnauthorized(config: AxiosRequestConfig | undefined): void {
  const url = buildUrl(config)
  if (url.includes(ADMIN_PATH_MARK)) clearAdminTokens()
  else clearMemberTokens()
  doRedirectToLogin()
}

// ==================================================================================
// ★★ 静默续期（2026-09-29）—— 解决「后台总是掉线」
//
// 背景：后台登录原先只签发 2 小时的 access token，**没有任何续期机制**，
//       而 access 存在 sessionStorage（每个标签页一份、关掉即失效）→
//       「2 小时到点掉」＋「重开标签页掉」两条叠起来，用户感受就是"总要重新登录"。
//
// 现在的行为：接口返回 401（或业务码 1002/1107）时，**先用 refresh token 换新令牌，
//          然后把这个请求原样重放一次**；只有续期也失败才清登录态跳登录页。
//
// 三条硬约束（每条都对应一种能把人锁死或放大损失的错法）：
//   ① **只重放一次**（`WD_RETRIED` 标记）：否则续期失败时会无限循环刷接口；
//   ② **续期请求自身不参与重试**：否则 refresh 被拒时又去 refresh，递归到栈溢出；
//   ③ **并发去重**（`refreshInFlight`）：多个请求同时 401 时只发一次续期 ——
//      refresh token 是**一次性轮换**的（用一次就作废），并发两个必然有一个失败，
//      失败那条会把用户踢去登录页（明明会话是好的）。
// ==================================================================================

/** 标记"这个请求已经因续期重放过一次"，挂在 config 上随请求走 */
const WD_RETRIED = '__wdRetriedAfterRefresh';

/** 正在进行中的续期（并发 401 共用同一个 Promise） */
let refreshInFlight: Promise<boolean> | null = null;

/** 是不是「后台」的请求（会员侧有自己的续期链路，这里只管后台） */
function isAdminUrl(url: string): boolean {
  return url.includes(ADMIN_PATH_MARK);
}

/** 认证类接口（登录/续期）自身不能参与"续期重试"，否则会递归 */
function isAuthEndpoint(url: string): boolean {
  return url.includes('/admin/auth/login') || url.includes('/admin/auth/refresh');
}

/**
 * 用 refresh token 换一对新令牌。**已经过期/被拒时返回 false**（不抛异常）。
 * 刻意用裸 axios（不走本文件的实例）：避免与拦截器互相递归。
 */
async function doSilentRefresh(baseURL: string): Promise<boolean> {
  const refreshToken = getAdminRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await http.post(
      `${baseURL}/admin/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    const body = res.data as ApiResponse<{ accessToken?: string; refreshToken?: string }>;
    const access = body?.code === ErrCode.SUCCESS ? body.data?.accessToken : undefined;
    if (!access) return false;
    // ★ 轮换：新 access + 新 refresh 一起存（旧 refresh 服务端已吊销）
    setAdminTokens(access, body.data?.refreshToken ?? null);
    return true;
  } catch {
    return false;
  }
}

/**
 * 供**启动引导**使用：没有 access 但有 refresh 时，先续期再继续。
 * 这条是「重开标签页/重启浏览器要重新登录」的根治点：
 * access 在 sessionStorage（新标签页没有），refresh 在 localStorage（还在），
 * 所以要在拉 profile 之前先把会话接回来。
 */
/**
 * 拿到一个可用会话。
 *
 * ⚠️ `force = true`（**401 路径必须传**）：那时旧的 access 还在，只是被服务端拒了；
 *    若因为"有 access"就直接返回 true，就不会续期、也不会跳登录，
 *    重放还会带着同一个死令牌再撞一次 401（单测就是这么抓出来的）。
 *
 * ⚠️ 启动引导用 `force = false`：有 access 就不必多打一次续期请求 ——
 *    更重要的是避免**多标签页各自启动时反复轮换 refresh**，把别的标签页的 refresh 挤掉。
 */
export async function ensureAdminSession(force = false): Promise<boolean> {
  if (!force && getAdminAccessToken()) return true;
  if (!getAdminRefreshToken()) return false;
  if (!refreshInFlight) {
    refreshInFlight = doSilentRefresh(axios.defaults.baseURL ?? '').finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * 401 后的"续期 + 重放原请求"。返回重放结果；不能重放时返回 null（交给上层走原有 401 流程）。
 */
async function retryAfterSilentRefresh(
  config: AxiosRequestConfig | undefined,
): Promise<AxiosResponse | null> {
  if (!config) return null;
  const url = buildUrl(config);
  // ① 已经重放过 → 不再重放（防死循环）
  if ((config as Record<string, unknown>)[WD_RETRIED]) return null;
  // ② 只处理后台请求；认证接口自身不参与
  if (!isAdminUrl(url) || isAuthEndpoint(url)) return null;

  // ★ 401 路径必须 force：旧 access 可能还在，不能因为"有令牌"就跳过续期
  const ok = await ensureAdminSession(true);
  if (!ok) return null;

  (config as Record<string, unknown>)[WD_RETRIED] = true;
  /**
   * 重放原请求：走同一个实例，于是**请求拦截器会重新取一次 access token**
   * （刚存进去的新令牌）—— 这是"续期生效"的关键，不要手工拼 Authorization 头。
   */
  return http.request(config);
}

// ---------------- 对外方法 ----------------

async function request<T>(
  config: AxiosRequestConfig,
  options: RequestOptions = {},
): Promise<T> {
  const { showError = true, rawEnvelope = false, headers, silent401 = false } = options
  try {
    const res = await http.request<ApiResponse<T>>({
      ...config,
      headers: { ...(config.headers as Record<string, string> | undefined), ...headers },
    })
    if (rawEnvelope) return res.data as unknown as T
    return res.data.data as T
  } catch (e) {
    const err = e instanceof ApiError ? e : new ApiError(ErrCode.SYSTEM_BUSY, '请求失败', buildUrl(config))
    if (showError && !(silent401 && err.code === ErrCode.UNAUTHORIZED)) {
      notifyError(err)
    }
    throw err
  }
}

/**
 * GET 请求。params 用 object 而不是 Record<string, unknown>：
 * 契约里的查询 DTO 都是 interface（没有索引签名），用 Record 会导致调用方全部需要强转。
 */
export function get<T>(url: string, params?: object, options?: RequestOptions): Promise<T> {
  return request<T>({ method: 'GET', url, params }, options)
}

export function post<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>({ method: 'POST', url, data }, options)
}

export function put<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>({ method: 'PUT', url, data }, options)
}

export function del<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>({ method: 'DELETE', url, data }, options)
}

/** 文件下载（导出差异清单 / 订单导出）：后端为文件流，不走统一包装解包 */
export async function download(url: string, params?: object): Promise<Blob> {
  const res = await http.request<Blob>({ method: 'GET', url, params, responseType: 'blob' })
  return res.data
}
